import React, { useState, useEffect, useRef, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as siteOperations,
  selectors as siteSelectors,
  actions as siteActions,
} from 'store/siteManager'
import { operations as licenseOperations } from 'store/licenseManager'
import { fetchUserContext } from 'auth/stores/userContext/operations'
import SecondStepApi from 'lib/api/secondStepApi'
import { adaptContextV4ToLegacyContext } from 'lib/api/adapters/context'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import { Formik } from 'formik'
import { pacingNodesData, validationSchema } from './data'

import PacingModalComponent from './component'

export const PacingModalContainer = ({
  clearError,
  currentStep: initCurrentStep,
  fetchUserSites,
  hasApiError,
  handleClose,
  implementationLevel,
  modals,
  nodeData,
  pacingDates,
  schoolYearEndDateString,
  site,
  updateSitePacingDates,
}) => {
  const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState(0)
  const isMountedRef = useRef(true)
  const effectiveImplementationLevel =
    implementationLevel === 0 ? 1 : implementationLevel

  const trainingStartNode =
    pacingDates?.find(pace => pace.nodeLevel === 'training') || null
  const programStartNode =
    pacingDates?.find(pace => pace.nodeKey === nodeData.node1) || null
  const trainingStartDate = trainingStartNode
    ? new Date(trainingStartNode.startDate)
    : null
  const programStartDate = programStartNode
    ? new Date(programStartNode.startDate)
    : null
  const schoolYearEndDate = useMemo(
    () => (schoolYearEndDateString ? new Date(schoolYearEndDateString) : null),
    [schoolYearEndDateString],
  )

  const { formikDates, unmodifiedDates } = useMemo(
    () =>
      Object.keys(nodeData).reduce(
        (dates, nodeName) => {
          const nodeKey = nodeData[nodeName]
          const nodeValue = pacingDates?.find(pace => pace.nodeKey === nodeKey)
          if (nodeValue) {
            dates.formikDates[nodeName] = new Date(nodeValue.startDate)
            dates.unmodifiedDates[nodeName] = new Date(nodeValue.startDate)
          }
          return dates
        },
        {
          formikDates: {},
          unmodifiedDates: {
            trainingStartDate: trainingStartNode
              ? new Date(trainingStartNode.startDate)
              : null,
            programStartDate: programStartNode
              ? new Date(programStartNode.startDate)
              : null,
            schoolYearEndDate: schoolYearEndDate,
          },
        },
      ),
    [
      nodeData,
      pacingDates,
      trainingStartNode,
      programStartNode,
      schoolYearEndDate,
    ],
  )

  const hasInitialValues =
    !!trainingStartNode && !!programStartDate && !!schoolYearEndDate

  const formatValuesToPacingDates = values =>
    Object.keys(values)
      .filter(key => key.includes('node'))
      .map(nodeName => {
        const key = nodeData[nodeName]
        const nodeToUpdate = pacingDates.find(node => node.nodeKey === key)
        const newNode = {
          ...nodeToUpdate,
          startDate:
            nodeName === 'nodeTraining'
              ? values.trainingStartDate
              : values[nodeName],
        }
        delete newNode.endDate
        return newNode
      })

  const submitEditedDates = async values => {
    const pacingDateUpdate = {
      pacingDates: formatValuesToPacingDates(values),
      programStartDate: values.programStartDate,
      schoolYearEndDate: values.schoolYearEndDate,
      trainingDate: values.trainingStartDate,
    }
    await updateSitePacingDates(site?.id, 'highSchool', pacingDateUpdate)
  }

  const submitInitialDates = async (values, touched) => {
    const hasDateChanged =
      touched.programStartDate ||
      touched.schoolYearEndDate ||
      touched.trainingStartDate

    const pacingDateUpdate = {
      pacingDates: pacingDates?.length ? formatValuesToPacingDates(values) : [],
      programStartDate: values.programStartDate,
      schoolYearEndDate: values.schoolYearEndDate,
      trainingDate: values.trainingStartDate,
    }
    if (hasDateChanged)
      await updateSitePacingDates(site?.id, 'highSchool', pacingDateUpdate)
  }

  const handleStepChange = toStep => setCurrentStep(toStep)
  const goBack = setFieldValue => {
    if (currentStep - 1 < 3) {
      setFieldValue('isEditingPacing', false)
    }
    handleStepChange(currentStep - 1)
  }

  const closeModal = async () => {
    clearError('pacingDates')

    // If v4 routing is enabled, refetch sites and user context instead of reloading the page
    if (isV4Routed('getUserSites') && isMountedRef.current) {
      try {
        await fetchUserSites()

        // Also refetch user context to ensure calculated preferences like isPacingComplete are updated
        if (isMountedRef.current) {
          const apiAddress =
            SecondStepApi.baseApiAddressForContext ||
            SecondStepApi.baseApiAddress
          const contextPath = apiAddress.endsWith('/v4/')
            ? 'context/me'
            : 'context'
          await fetchUserContext(dispatch, apiAddress, {
            contextPath,
            adaptResponse:
              contextPath === 'context/me'
                ? adaptContextV4ToLegacyContext
                : undefined,
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to fetch sites/user context after pacing modal completion:',
          error,
        )
      }
    }

    // Only close if component is still mounted
    if (isMountedRef.current) {
      handleClose()
    }
  }

  const handleContinueButtonClick = async (values, touched, setFieldValue) => {
    if (currentStep + 1 === 2) {
      await submitInitialDates(values, touched)
    }
    if (currentStep + 1 === 4) {
      await submitEditedDates(values)
      handleStepChange(currentStep - 1)
      setFieldValue('isEditingPacing', false)
      return
    }

    handleStepChange(currentStep + 1)
    if (currentStep + 1 === 3) {
      setFieldValue('isEditingPacing', true)
    } else {
      setFieldValue('isEditingPacing', false)
    }
  }

  // Handle closing from ReviewPacing step (step 2) - ensure pacing is saved
  const handleReviewConfirm = async (values, touched) => {
    // Use unmodifiedDates (which are displayed in ReviewPacing) as fallback for form values
    // This ensures we always have dates even if the form values are null
    const finalProgramStartDate =
      values.programStartDate || unmodifiedDates.programStartDate
    const finalSchoolYearEndDate =
      values.schoolYearEndDate || unmodifiedDates.schoolYearEndDate
    const finalTrainingDate =
      values.trainingStartDate || unmodifiedDates.trainingStartDate

    // Only save if we have all required dates (all must be Date objects, not null)
    if (
      finalProgramStartDate instanceof Date &&
      finalSchoolYearEndDate instanceof Date &&
      finalTrainingDate instanceof Date
    ) {
      const pacingDateUpdate = {
        pacingDates: pacingDates?.length
          ? formatValuesToPacingDates(values)
          : [],
        programStartDate: finalProgramStartDate,
        schoolYearEndDate: finalSchoolYearEndDate,
        trainingDate: finalTrainingDate,
      }
      await updateSitePacingDates(site?.id, 'highSchool', pacingDateUpdate)
    }

    // Then close the modal
    await closeModal()
  }

  useEffect(() => {
    setCurrentStep(initCurrentStep || 0)
  }, [initCurrentStep])

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return (
    <Formik
      enableReinitialize={true}
      initialTouched={{
        trainingStartDate: !!trainingStartDate,
        programStartDate: !!programStartDate,
        schoolYearEndDate: !!schoolYearEndDate,
      }}
      initialValues={{
        trainingStartDate,
        programStartDate,
        schoolYearEndDate,
        implementationLevel: effectiveImplementationLevel,
        isEditingPacing: false,
        ...formikDates,
      }}
      isInitialValid={hasInitialValues}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isValid,
      }) => (
        <PacingModalComponent
          currentStep={currentStep}
          errors={errors}
          goBack={() => goBack(setFieldValue)}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={() => {
            closeModal()
          }}
          handleContinueButtonClick={() =>
            handleContinueButtonClick(values, touched, setFieldValue)
          }
          handleReviewConfirm={
            currentStep === 2
              ? () => handleReviewConfirm(values, touched)
              : undefined
          }
          handleStepChange={handleStepChange}
          handleSubmit={handleSubmit}
          hasApiError={hasApiError}
          hasInitialValues={hasInitialValues}
          implementationLevel={effectiveImplementationLevel}
          isValid={isValid}
          modals={modals}
          touched={touched}
          unmodifiedDates={{
            ...unmodifiedDates,
            // Use Formik values if they exist, otherwise fall back to unmodifiedDates from store
            trainingStartDate:
              values.trainingStartDate || unmodifiedDates.trainingStartDate,
            programStartDate:
              values.programStartDate || unmodifiedDates.programStartDate,
            schoolYearEndDate:
              values.schoolYearEndDate || unmodifiedDates.schoolYearEndDate,
          }}
          values={values}
        />
      )}
    </Formik>
  )
}

export const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors?.selectSiteById(state, ownProps.siteId)
  const pacingDates = site?.pacing?.highSchool || site?.pacing?.highschool || []
  const implementationLevel =
    site?.sitePreferences?.highSchool?.implementationLevel
  const nodeData = pacingNodesData.highSchool
  const { pacingDates: pacingDatesAPIResults } = state.siteManager
  const { error } = pacingDatesAPIResults

  return {
    hasApiError: !!error,
    implementationLevel,
    nodeData,
    pacingDates,
    schoolYearEndDateString: site?.schoolYearEndDate,
    site,
  }
}

const mapDispatchToProps = {
  updateSitePacingDates: siteOperations.updateSitePacingDates,
  clearError: siteActions.clearError,
  fetchUserSites: licenseOperations.fetchUserSites,
}

PacingModalContainer.propTypes = {
  clearError: PropTypes.func,
  currentStep: PropTypes.number,
  dispatch: PropTypes.func,
  fetchUserSites: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  implementationLevel: PropTypes.number,
  modals: PropTypes.array,
  nodeData: PropTypes.object,
  pacingDates: PropTypes.array,
  schoolYearEndDateString: PropTypes.string,
  site: PropTypes.shape({
    address: PropTypes.object,
    id: PropTypes.number,
    isRostered: PropTypes.bool,
    name: PropTypes.string,
    netSuiteCustomerId: PropTypes.number,
    schoolYearEndDate: PropTypes.string,
    sitePreferences: PropTypes.object,
  }),
  siteId: PropTypes.number,
  updateSitePacingDates: PropTypes.func,
}

const mapper = entry => {
  const { modals } = entry || {}
  return {
    modals,
  }
}

const options = {
  entryId: '6PHmIWTvI3XGF6siazlac5',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    PacingModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
